import { urlPathGet } from "common/url";
import PageError from "components/page-error";
import PageWrapper from "components/page-wrapper";
import { Suspense, useCallback } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAppSelector } from "store";
import { authNewSelectors } from "store/reducers/auth-new";

import paths from "./paths";
import { RequireAuth } from "./require-auth";
import { RequireTeamOwner } from "./require-team-owner";
import routeList, { RouteType } from "./route-list";

const Router = () => {
  const isLoggedIn = useAppSelector(authNewSelectors.loggedInGet);
  const isTeamOwner = useAppSelector(authNewSelectors.userIsTeamOwner);

  const generator = useCallback(
    (path: string, route: RouteType) => {
      const requireAuth = ["require-team-owner", "required"].includes(
        route.auth || "",
      );

      let element = route.component;
      if (route.auth === "guest-only" && isLoggedIn) {
        const urlParams = new URLSearchParams(window.location.search);
        const successPath = urlPathGet(urlParams.get("successUrl"));
        element = <Navigate to={successPath || paths.searchSingle()} />;
      } else if (requireAuth && !isLoggedIn) {
        element = (
          <RequireAuth signup={route.authRequiredRedirectTo === "signup"} />
        );
      } else if (route.auth === "require-team-owner" && !isTeamOwner) {
        element = <RequireTeamOwner />;
      }

      return (
        <Route element={element} key={path} path={path}>
          {Object.entries(route.children ?? []).map(([childPath, childRoute]) =>
            generator(childPath, childRoute),
          )}
        </Route>
      );
    },
    [isLoggedIn, isTeamOwner],
  );

  return (
    <Suspense fallback={<PageWrapper loading />}>
      <Routes>
        <Route element={<PageError code={404} />} path="*" />
        {Object.entries(routeList).map(([path, route]) =>
          generator(path, route),
        )}
      </Routes>
    </Suspense>
  );
};

export default Router;
